import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { api, ApiError } from "@/lib/api";
import { oauthResumeResponse, publicClientResponse, selectWorkspaceResponse } from "@sitebrew/contracts/v1";
import type { z } from "zod";
import { useMe } from "@/lib/session";
import { BusyButton, ButtonLink } from "@/components/button";
import { LoginCard, LoginCardSubtitle, LoginCardTitle, Notice } from "@/components/ui/login-card";

/**
 * Which workspace an OAuth grant acts in — asked every time an account has
 * more than one workspace, for whichever `client_id` is authorizing right
 * now (`auth.ts`'s `postLogin` hook decides that, unconditionally; it used to
 * skip this screen once a *browser session* had already picked, which is
 * exactly what let a second "Connect another account" for a different
 * `client_id` silently reuse the first one's workspace — live-repro'd by
 * Jakub, 2026-09-18).
 *
 * The library's own extension point for exactly this shape (its own doc
 * comment gives "allow selection of an organization or team" as the worked
 * example), so the shape of the page is theirs, not invented here: land with
 * the whole original authorization query as this page's own query string
 * (signed by the server, opaque to this screen — it is handed back verbatim,
 * never parsed apart), let the person choose, then resume the flow through
 * `/oauth2/continue` the same way {@link ConsentScreen} resumes through
 * `/oauth2/consent` after *Allow*.
 *
 * `POST /v1/oauth/select-workspace` records the choice against **this
 * client** first — a separate call, before `/oauth2/continue` — because the
 * choice has to be readable by `auth.ts`'s `consentReferenceId` hook, which
 * runs inside the library's own request and has no way to read anything this
 * screen's own request body carried, nor which `client_id` it carried it for
 * (`node_modules/@better-auth/oauth-provider`'s own types hand that hook only
 * `{ user, session }`). `clientId` — already read off the authorize query
 * string this screen lands on, previously only to fetch the client's display
 * name below — is what makes the grant this call writes belong to the right
 * client rather than to whichever one this browser asked about last.
 */

/** `/oauth2/continue` answers the same `oauthResumeResponse` as `/oauth2/consent` (`@sitebrew/contracts/v1`, read from the library's own `consentEndpoint`/`continueEndpoint` — see {@link ConsentScreen}); this is the client the server knows, for the same endpoint {@link ConsentScreen} asks. */
type PublicClient = z.infer<typeof publicClientResponse>;

export function SelectWorkspaceScreen({ search, clientId }: { search: string; clientId: string | undefined }) {
  const me = useMe();
  const workspaces = me.data?.workspaces ?? [];
  const navigate = useNavigate();
  const [client, setClient] = useState<PublicClient | null>(null);

  /**
   * The client's self-declared name, if the server knows one — fetched the
   * same way {@link ConsentScreen} fetches it, and for the same reason this
   * screen needs it at all: picking a workspace here is the same trust
   * decision consent protects (both hand an application access to it), so a
   * person choosing here deserves to see *which* application is asking, not
   * just "an application". A failure here leaves the name absent rather than
   * blocking the screen — the workspace list is what the decision rests on.
   */
  useEffect(() => {
    if (!clientId) return;
    let current = true;
    api(`/auth/oauth2/public-client?client_id=${encodeURIComponent(clientId)}`, publicClientResponse)
      .then((found) => {
        if (current) setClient(found);
      })
      .catch(() => {
        // Deliberately silent, same as ConsentScreen: an unnamed client is a
        // screen with less on it, not an error the person can act on.
      });
    return () => {
      current = false;
    };
  }, [clientId]);

  /**
   * This screen sits outside `authedRoute` on purpose (see `app.tsx`'s own
   * comment on `selectWorkspaceRoute`: a decision another program is waiting
   * on should not sit inside the product's navigation), so it does not get
   * that layout's `Authed` component and its session-loss effect for free —
   * it has to repeat the one property that matters here: `beforeLoad` only
   * decided who could *arrive*, not who may *stay*. A session ending while
   * this screen is mounted (sign-out in another tab, an expired cookie found
   * by the next refetch) would otherwise leave `me.data` at `null` and
   * `workspaces` silently empty, and the screen below reads that as "you are
   * not a member of any workspace yet" — which is false and actively
   * misleading; the true state is "you were signed out". `next` carries this
   * screen's own address, the same shape `consentRoute`/`selectWorkspaceRoute`
   * already send an unauthenticated *arrival* to sign in with.
   */
  useEffect(() => {
    if (me.data === null) {
      void navigate({ to: "/login", search: { next: `/select-workspace?${search}` } });
    }
  }, [me.data, navigate, search]);

  const [selected, setSelected] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  // The one workspace an account could somehow have here (a race with
  // `shouldRedirect`'s own more-than-one check, run moments earlier) arrives
  // pre-selected rather than requiring a click nobody needs to make — but
  // still through the ordinary *Continue* button, not a submit this effect
  // fires on its own: a screen that can navigate itself the instant data
  // loads is a screen a slow connection submits before its own radio list has
  // painted.
  useEffect(() => {
    if (workspaces.length === 1) setSelected(workspaces[0]!.id);
  }, [workspaces]);

  const continueWith = async (workspaceId: string) => {
    // Reachable only if the authorize query string this screen landed on was
    // somehow missing `client_id` — `auth.ts`'s `postLogin.page` redirect
    // always carries the original authorization query verbatim, so this is a
    // defensive refusal, not an expected path: the API route requires
    // `clientId` too, and would answer the same `400` this pre-empts, just
    // after a round trip instead of before one.
    if (!clientId) {
      setFailure(
        "This screen does not know which application is asking. Start the connection again from the application.",
      );
      return;
    }

    setPending(true);
    setFailure(null);
    try {
      await api("/oauth/select-workspace", selectWorkspaceResponse, { method: "POST", body: { workspaceId, clientId } });

      const answered = await api("/auth/oauth2/continue", oauthResumeResponse, {
        method: "POST",
        body: { postLogin: true, oauth_query: search },
      });

      // The same defensive read `ConsentScreen` does for the same reason: a
      // recorded choice with nowhere to send the browser back to is a
      // person left on a blank success with no way to finish connecting.
      if (!answered?.url) {
        setPending(false);
        setFailure(
          "Your choice was recorded, but the application did not give us an address to return you to. Start the connection again from the application.",
        );
        return;
      }

      window.location.assign(answered.url);
    } catch (err) {
      setPending(false);
      setFailure(
        err instanceof ApiError
          ? err.message
          : "The workspace could not be selected. Try connecting again.",
      );
    }
  };

  return (
    <LoginCard>
      <LoginCardTitle>Which workspace?</LoginCardTitle>
      <LoginCardSubtitle>
        {client?.name ? (
          <>
            <span className="font-medium">{client.name}</span> is asking to act in one of your
            workspaces. Choose which one.
          </>
        ) : (
          "An application is asking to act in one of your workspaces. Choose which one."
        )}
      </LoginCardSubtitle>

      {workspaces.length === 0 ? (
        <Notice>You are not a member of any workspace yet.</Notice>
      ) : (
        <fieldset className="my-4 space-y-2">
          <legend className="sr-only">Workspace</legend>
          {workspaces.map((workspace) => (
            <label
              key={workspace.id}
              className="flex cursor-pointer items-center gap-3 rounded-lg border border-border p-3 text-sm has-[:checked]:border-primary"
            >
              <input
                type="radio"
                name="workspace"
                value={workspace.id}
                checked={selected === workspace.id}
                onChange={() => setSelected(workspace.id)}
                disabled={pending}
              />
              <span className="font-medium">{workspace.name}</span>
            </label>
          ))}
        </fieldset>
      )}

      {failure ? <Notice>{failure}</Notice> : null}

      <div className="mt-4 flex justify-between gap-3">
        {/*
          * Disabled while a choice is in flight — clicking away mid-request
          * would abandon whichever POST has not settled yet after the first may
          * have already succeeded (the same two-POST sequence `continueWith`
          * below is not atomic about), the same way the *Continue* button
          * below already refuses a second click during `pending`.
          */}
        <ButtonLink
          className={pending ? "pointer-events-none opacity-50" : undefined}
          aria-disabled={pending}
          href="/"
        >
          Back to your websites
        </ButtonLink>

        <BusyButton
          type="button"
          busy={pending}
          disabled={pending || !selected}
          onClick={() => selected && void continueWith(selected)}
        >
          Continue
        </BusyButton>
      </div>
    </LoginCard>
  );
}
